import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, LineChart, BadgeIndianRupee, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { useSite } from "@/lib/site";
import { DealGrid, DealGridSkeleton } from "@/components/site/DealCard";
import { SectionHead, CouponCard } from "@/components/site/bits";
import { TopStores, FestiveBanner } from "@/components/site/TopStores";
import { JoinTelegram } from "@/components/site/JoinTelegram";

const HERO_IMG = "https://images.unsplash.com/photo-1542351967-d5ae722fed71?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

const Hero = () => (
  <section className="relative bg-emerald-900 text-white overflow-hidden" data-testid="home-hero">
    <div className="absolute inset-0 hero-grain" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid md:grid-cols-[1.2fr_1fr] gap-10 items-center">
      <div className="space-y-6">
        <span className="rise inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-300">
          <ShieldCheck size={14} /> No fake timers · No made-up reviews
        </span>
        <h1 className="rise rise-1 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
          Deals worth your rupee, <span className="text-amber-300">checked</span> before you click.
        </h1>
        <p className="rise rise-2 text-emerald-100/90 text-base md:text-lg max-w-xl">
          We track prices across Amazon, Flipkart, Myntra, Ajio, Nykaa and more - and score every offer on real discount, price history and freshness.
        </p>
        <div className="rise rise-3 flex flex-wrap gap-3">
          <Link to="/deals" data-testid="hero-browse-deals-button" className="bg-amber-400 hover:bg-amber-300 text-emerald-950 font-extrabold px-6 py-3 rounded-full inline-flex items-center gap-2 transition-colors">
            Browse today's deals <ArrowRight size={18} />
          </Link>
          <Link to="/coupons" data-testid="hero-coupons-button" className="border border-emerald-400/50 hover:bg-emerald-800 px-6 py-3 rounded-full font-bold transition-colors">Coupon codes</Link>
        </div>
      </div>
      <div className="rise rise-2 relative hidden md:block">
        <img src={HERO_IMG} alt="Shopping bags" className="rounded-2xl h-80 w-full object-cover shadow-2xl" />
        <div className="absolute -bottom-5 -left-5 bg-white text-slate-900 rounded-xl p-4 shadow-xl w-56">
          <p className="text-xs text-slate-500 font-semibold">How we score deals</p>
          <p className="font-display font-extrabold text-lg">Discount · History · Freshness</p>
        </div>
      </div>
    </div>
  </section>
);

const Pills = ({ cats }) => (
  <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1" data-testid="home-category-pills">
    {cats.map((c) => (
      <Link key={c.id} to={`/category/${c.slug}`} data-testid={`category-pill-${c.slug}`}
        className="shrink-0 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold hover:border-emerald-500 hover:text-emerald-700 transition-colors">
        {c.name}
      </Link>
    ))}
  </div>
);

const Trust = () => (
  <div className="grid sm:grid-cols-3 gap-4" data-testid="home-trust-strip">
    {[[ShieldCheck, "Clearly labelled links", "Every “Get Deal” button is an affiliate link and we say so."],
      [LineChart, "Price history, not hype", "We only call a price low when our recorded history supports it."],
      [BadgeIndianRupee, "Prices in INR", "MRP, selling price and discount shown side by side."]].map(([Icon, t, d]) => (
      <div key={t} className="bg-white rounded-xl border border-slate-200 p-5 flex gap-4">
        <Icon className="text-emerald-600 shrink-0" />
        <div><p className="font-bold">{t}</p><p className="text-sm text-slate-500">{d}</p></div>
      </div>
    ))}
  </div>
);

export default function Home() {
  const site = useSite();
  useSeo({ title: "" });
  const { data, isLoading } = useQuery({ queryKey: ["home"], queryFn: () => api.get("/public/home").then((r) => r.data) });
  const wrap = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
  return (
    <div data-testid="home-page">
      <Hero />
      <div className={`${wrap} -mt-6 relative`}>{data && <Pills cats={data.categories} />}</div>
      <div className={`${wrap} space-y-16 mt-12`}>
        <FestiveBanner />
        {(isLoading || data?.best?.length > 0) && (
          <section>
            <SectionHead title="Today's best deals" sub={`Highest ${site.name} score right now`} to="/deals" testid="section-best" />
            {isLoading ? <DealGridSkeleton /> : <DealGrid deals={data.best} testid="best-deals-grid" />}
          </section>
        )}
        {data?.trending?.length > 0 && (
          <section>
            <SectionHead title="Trending this week" sub="Most clicked by shoppers in the last 7 days" testid="section-trending" />
            <DealGrid deals={data.trending.slice(0, 4)} testid="trending-deals-grid" />
          </section>
        )}
        {data?.coupons?.length > 0 && (
          <section>
            <SectionHead title="Coupon codes & offers" to="/coupons" testid="section-coupons" />
            <div className="grid md:grid-cols-2 gap-4">{data.coupons.slice(0, 4).map((c) => <CouponCard key={c.id} c={c} />)}</div>
          </section>
        )}
        <JoinTelegram variant="strip" />
        <TopStores />
        {data?.biggest?.length > 0 && (
          <section>
            <SectionHead title="Biggest discounts" sub="Sorted by % off MRP" to="/deals?sort=discount" testid="section-biggest" />
            <DealGrid deals={data.biggest.slice(0, 4)} testid="biggest-deals-grid" />
          </section>
        )}
        {data && (
          <section>
            <SectionHead title="Latest offers" to="/deals?sort=newest" testid="section-latest" />
            <DealGrid deals={data.latest.slice(0, 4)} testid="latest-deals-grid" />
          </section>
        )}
        <Trust />
        {data?.guides?.length > 0 && (
          <section>
            <SectionHead title="Buying guides" to="/guides" testid="section-guides" />
            <div className="grid md:grid-cols-3 gap-6">
              {data.guides.map((g) => (
                <Link key={g.id} to={`/guide/${g.slug}`} data-testid={`guide-card-${g.slug}`} className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <img src={g.cover_url} alt="" className="h-40 w-full object-cover" loading="lazy" />
                  <div className="p-5"><p className="font-bold group-hover:text-emerald-700">{g.title}</p><p className="text-sm text-slate-500 mt-1 line-clamp-2">{g.excerpt}</p></div>
                </Link>
              ))}
            </div>
          </section>
        )}
        {data?.merchants?.length > 0 && (
          <section>
            <SectionHead title="All stores" to="/stores" testid="section-stores" />
            <div className="flex flex-wrap gap-3">
              {data.merchants.map((m) => (
                <Link key={m.id} to={`/store/${m.slug}`} data-testid={`store-chip-${m.slug}`} className="px-5 py-3 bg-white border border-slate-200 rounded-xl font-bold hover:border-emerald-500 transition-colors">{m.name}</Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
